import { vestingPillHref } from './vesting-pill';
import classNames from 'classnames';
import { useEffect, useMemo, useRef, useState, type FC } from 'react';
import {
  Button,
  Dropdown,
  InlineLoading,
  InlineNotification,
  MenuButton,
  MenuItem,
  Modal,
  Tag,
} from '@carbon/react';
import {
  Add,
  Checkmark,
  DocumentSubtract,
  Renew,
  Undo,
} from '@carbon/react/icons';
import { useVestingDocumentActions } from 'components/vesting-documents/vesting-document-actions';
import { DataTable, type DataTableColumnDef } from 'components/data-table';
import {
  relatedItemColumns,
  type RelatedItemRow,
} from 'components/related-items/related-items-section';
import type { RelatedItem } from 'components/related-items/types';
import { DestroyConfirmation } from 'components/destroy-confirmation';
import { DirectSupportTag } from 'components/related-items/external-title-support-picker';
import { LinkDocumentPanel } from 'components/related-items/link-document-panel';
import { Link } from 'components/link';
import { VestingDocumentDetail } from 'components/related-items/vesting-document-detail';
import { TrashButton } from 'components/trash-button';
import {
  VestingDocumentSection,
  type VestingDocumentHandle,
} from 'components/vesting-documents/vesting-document-section';
import { WorkspaceCard, WorkspaceCardNote } from 'components/workspace-cards';
import {
  EMPTY,
  vestingDocumentLabel,
} from 'components/vesting-documents/types';
import { col } from 'components/vesting-documents/citation-columns';
import {
  VestingStatusTag,
  vestingStatus,
} from 'components/vesting-documents/vesting-document-card';
import {
  readWriteFailure,
  UNEXPLAINED_FAILURE,
} from 'pages/well-workspaces/utils';
import { format0 } from 'utils/number';
import {
  deletePendingChange,
  deleteSupport,
  patchStagedVerification,
  postSupportRetry,
  postTracts,
  useRefreshWorkspace,
  useWorkspaceWrite,
  workspacePath,
} from '../api';
import {
  EMPTY_FOUND,
  TractFinder,
  TractsReview,
  tractColumns as crmTractColumns,
  type FoundTracts,
} from '../pickers';
import type {
  OwnershipRow,
  WorkspaceEnvelope,
  WorkspaceSupport,
  WorkspaceTract,
  WorkspaceVestingRow,
} from '../types';
import {
  DOCUMENT_COLUMNS,
  MOR_COLUMNS,
  PENDING_REMOVAL,
  PIN_ACTIONS,
  RowAction,
  VestingPill,
  actionsCol,
  awaitingVerify,
  confirmProps,
  groupByDocument,
  shownVesting,
  tractHref,
  vestingPillLabel,
  type DocumentRow,
  type TractRow,
} from './columns';
import { usePagedTable } from './comparisons';
import {
  matchesFilters,
  toColumnFilters,
  useReportRows,
  type ReportRows,
  type TableFilters,
} from './filters';
import pageStyles from '../page.module.scss';
import styles from './page.module.scss';

export const isConverting = (support: WorkspaceSupport) =>
  support.convertedAt === null && support.conversionError === null;

// A draft's vesting changes wait on the workspace; promote applies them to the shared vesting documents.
const useStagedVerification = (id: string) => {
  const stage = useWorkspaceWrite((row: WorkspaceVestingRow) =>
    patchStagedVerification(row.href)
  );
  const unstage = useWorkspaceWrite((changeId: number) =>
    deletePendingChange(id, 'vesting', changeId)
  );
  const { mutate: stageMutate, reset: stageReset } = stage;
  const { mutate: unstageMutate, reset: unstageReset } = unstage;
  const busy = stage.isPending || unstage.isPending;
  const error = readWriteFailure(
    stage.error ?? unstage.error,
    UNEXPLAINED_FAILURE
  );
  // Memoized so the table rebuilds its columns only when a write starts, ends or fails.
  return useMemo(
    () => ({
      stage: (row: WorkspaceVestingRow) => stageMutate(row),
      unstage: ({ pendingChangeId }: WorkspaceVestingRow) => {
        if (pendingChangeId !== null) unstageMutate(pendingChangeId);
      },
      busy,
      error,
      clearError: () => {
        stageReset();
        unstageReset();
      },
    }),
    [stageMutate, unstageMutate, stageReset, unstageReset, busy, error]
  );
};

const TractVestingPanel: FC<{
  workspaceId: string;
  tract: WorkspaceTract;
  readOnly: boolean;
  onChanged: () => void;
  staging: ReturnType<typeof useStagedVerification>;
}> = ({ workspaceId, tract, readOnly, onChanged, staging }) => {
  const sectionRef = useRef<VestingDocumentHandle>(null);
  const [ready, setReady] = useState(false);
  const [pending, setPending] = useState(false);
  const [adding, setAdding] = useState(false);
  const documents = tract.vestingDocuments;
  const searching = !readOnly && (documents.length === 0 || adding);

  return (
    <div className={classNames(styles.heldToCard, pageStyles.expanded)}>
      {documents.length ? (
        documents.map((row) => (
          <div key={row.key}>
            {row.pending && (
              <div className={styles.actions}>
                {row.pending === 'remove' && (
                  <Tag type="teal" size="sm">
                    {PENDING_REMOVAL}
                  </Tag>
                )}
                {!readOnly && (
                  <Button
                    size="sm"
                    kind="ghost"
                    renderIcon={Undo}
                    disabled={staging.busy}
                    onClick={() => staging.unstage(row)}
                  >
                    Undo
                  </Button>
                )}
              </div>
            )}
            <VestingDocumentDetail
              document={{ details: { ...row } }}
              readOnly={readOnly || !!row.pending}
              onChanged={onChanged}
              verification={{
                staged: row.staged,
                verifying: staging.busy,
                onVerify: () => staging.stage(row),
                onUndo: () => staging.unstage(row),
              }}
            />
          </div>
        ))
      ) : (
        <WorkspaceCardNote>No vesting document yet.</WorkspaceCardNote>
      )}
      {searching && (
        <VestingDocumentSection
          ref={sectionRef}
          host={{
            basePath: `${workspacePath(workspaceId)}/tracts/${
              tract.salesforceTractId
            }`,
          }}
          context={{
            referenceLocationId: tract.referenceLocationId ?? undefined,
            referenceLocationName: tract.referenceLocationName,
            buyerEntity: tract.buyerEntity,
            county:
              tract.countyId && tract.countyName
                ? { id: tract.countyId, name: tract.countyName }
                : null,
          }}
          multiple
          onReadyChange={setReady}
          onPendingChange={setPending}
          onSaved={() => {
            setAdding(false);
            onChanged();
          }}
        />
      )}
      {!readOnly && (
        <div className={styles.actions}>
          {searching ? (
            <Button
              size="sm"
              disabled={!ready || pending}
              onClick={() => sectionRef.current?.submit()}
            >
              Select documents
            </Button>
          ) : (
            <Button size="sm" kind="tertiary" onClick={() => setAdding(true)}>
              Add document
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

const TRACT_COLUMN_IDS = new Set([
  'name',
  'entityName',
  'accountName',
  'surveyArea',
  'quarterCall',
  'netAcres',
  'netAcreageUnits',
  'grossAcres',
  'instrumentType',
  'targetNpvIncluded',
  'vestingDocument',
]);

const TRACT_LEVELS = { tracts: 'Tracts', documents: 'Vesting Documents' };
type TractLevel = keyof typeof TRACT_LEVELS;

const toTractRow = (tract: WorkspaceTract): TractRow => {
  const shown = shownVesting(tract);
  return {
    ...tract,
    id: tract.salesforceTractId,
    entities:
      tract.entityId && tract.entityName
        ? [{ id: tract.entityId, name: tract.entityName }]
        : [],
    vestingDocument: shown && {
      verified: shown.verified,
      provenance: shown.provenance,
      documentHref: shown.document.href,
    },
  };
};

interface CardProps {
  id: string;
  detail: WorkspaceEnvelope;
}

interface FilteredProps {
  filters: TableFilters;
  onRows: ReportRows;
}

// "Tracts (2)" never breaks before its count when a drawer narrows the card.
const counted = (title: string, count: number) =>
  `${title}\u00a0(${format0(count)})`;

const AddTractsModal: FC<{
  id: string;
  open: boolean;
  onClose: () => void;
  existing: Set<string>;
}> = ({ id, open, onClose, existing }) => {
  // Kept while the modal is closed, so reopening it resumes the search.
  const [found, setFound] = useState<FoundTracts>(EMPTY_FOUND);
  const [reviewing, setReviewing] = useState(false);
  const add = useWorkspaceWrite((ids: string[]) => postTracts(id, ids));
  const close = () => {
    setReviewing(false);
    add.reset();
    onClose();
  };
  // A pick the workspace gained since, which the server would skip, is not counted.
  const picked = useMemo(
    () => ({
      ...found,
      ids: found.ids.filter((tract) => !existing.has(tract)),
    }),
    [found, existing]
  );

  return (
    <Modal
      open={open}
      size="lg"
      modalHeading="Add tracts"
      className={styles.tearsheet}
      primaryButtonText={
        !reviewing ? 'Review' : add.isPending ? 'Adding…' : 'Add'
      }
      secondaryButtonText={reviewing ? 'Back' : 'Cancel'}
      primaryButtonDisabled={picked.ids.length === 0 || add.isPending}
      onRequestSubmit={() => {
        if (!reviewing) return setReviewing(true);
        add.mutate(picked.ids, {
          onSuccess: () => {
            setFound(EMPTY_FOUND);
            close();
          },
        });
      }}
      onSecondarySubmit={() => (reviewing ? setReviewing(false) : close())}
      onRequestClose={close}
      hasScrollingContent
    >
      {add.error ? (
        <InlineNotification
          kind="error"
          lowContrast
          title="Error"
          subtitle={
            readWriteFailure(
              add.error,
              `Could not add the tracts. ${UNEXPLAINED_FAILURE}`
            ) ?? undefined
          }
          onCloseButtonClick={() => add.reset()}
        />
      ) : null}
      {open &&
        (reviewing ? (
          <TractsReview found={picked} />
        ) : (
          <TractFinder
            found={found}
            onChange={setFound}
            workspaceId={id}
            existing={existing}
          />
        ))}
    </Modal>
  );
};

export const TractsCard: FC<
  CardProps &
    FilteredProps & {
      onRemoveTract: (tract: WorkspaceTract) => void;
    }
> = ({ id, detail, onRemoveTract, filters, onRows }) => {
  const refresh = useRefreshWorkspace();
  const staging = useStagedVerification(id);
  const columnFilters = useMemo(() => toColumnFilters(filters), [filters]);
  const { tracts } = detail.titleSupport;
  const {
    askRemove: askRemoveVesting,
    error: vestingError,
    clearError: clearVestingError,
    confirmation: removeVestingConfirmation,
  } = useVestingDocumentActions(refresh);
  const tractRows = useMemo(() => tracts.map(toTractRow), [tracts]);
  const tractIds = useMemo(
    () => new Set(tracts.map((tract) => tract.salesforceTractId)),
    [tracts]
  );
  useReportRows('tracts', onRows, tractRows);
  const tractColumns = useMemo(() => {
    const crm = (
      crmTractColumns().filter((column) =>
        TRACT_COLUMN_IDS.has(column.id as string)
      ) as unknown as DataTableColumnDef<TractRow>[]
    ).map((column) => {
      if (column.id !== 'vestingDocument') return column;
      return {
        ...column,
        meta: {
          ...column.meta,
          getExportValue: (row: unknown) => {
            const { vestingDocument, vestingState } = row as TractRow;
            return vestingPillLabel(vestingDocument, vestingState);
          },
          getExportHref: (row: unknown) => {
            const { vestingDocument, vestingState } = row as TractRow;
            return vestingPillHref(vestingDocument, vestingState);
          },
        },
        cell: ({ row }: { row: { original: TractRow } }) => (
          <VestingPill
            indicator={row.original.vestingDocument}
            state={row.original.vestingState}
          />
        ),
      };
    });
    const county = col<TractRow>('countyName', 'County', 160, {
      type: 'enum',
    });
    if (detail.promoted) return [...crm, county];
    const actions = actionsCol<TractRow>(136, ({ row }) => {
      const tract = row.original;
      const unverified = awaitingVerify(tract);
      const staged = !!unverified?.staged;
      return (
        <div className={styles.rowActions}>
          {unverified && (
            <>
              <RowAction
                renderIcon={staged ? Undo : Checkmark}
                iconDescription={
                  staged ? 'Undo verify' : 'Verify vesting document on promote'
                }
                disabled={staging.busy}
                onClick={() => {
                  if (staged) staging.unstage(unverified);
                  else staging.stage(unverified);
                }}
              />
              <RowAction
                renderIcon={DocumentSubtract}
                iconDescription="Remove vesting document"
                onClick={() => askRemoveVesting(unverified)}
              />
            </>
          )}
          <TrashButton
            kind="ghost"
            size="sm"
            description="Remove tract"
            tooltipPosition="left"
            tooltipAlignment="center"
            onClick={() => onRemoveTract(tract)}
          />
        </div>
      );
    });
    return [...crm, county, actions];
  }, [detail.promoted, onRemoveTract, staging, askRemoveVesting]);
  const [level, setLevel] = useState<TractLevel>('tracts');
  const [adding, setAdding] = useState(false);
  // The rail's tract filters hold at the document level too.
  const documentRows = useMemo(
    () =>
      groupByDocument(tractRows.filter((row) => matchesFilters(row, filters))),
    [tractRows, filters]
  );
  const levelDropdown = (
    <Dropdown<TractLevel>
      key="tsw-tract-level"
      id="tsw-tract-level"
      className={styles.levelDropdown}
      titleText="Aggregation"
      hideLabel
      label="Aggregation"
      items={Object.keys(TRACT_LEVELS) as TractLevel[]}
      itemToString={(item) => (item ? TRACT_LEVELS[item] : '')}
      selectedItem={level}
      onChange={({ selectedItem }) => selectedItem && setLevel(selectedItem)}
      size="sm"
    />
  );
  const tractTable = (rows: TractRow[], nested = false) => (
    <DataTable<TractRow>
      data={rows}
      columns={tractColumns}
      getRowId={(row) => row.id}
      config={{
        columnFilters: false,
        globalSearch: !nested,
        expandAllToggle: !nested,
        sizeToContent: true,
        // Exports the rows the rail's filters leave; nested, the group's tracts.
        export: true,
      }}
      initialTableState={PIN_ACTIONS}
      controlledState={nested ? undefined : { columnFilters }}
      leftToolbarActions={nested ? undefined : [levelDropdown]}
      renderRowSubComponent={(row) => (
        <TractVestingPanel
          workspaceId={id}
          tract={row.original}
          readOnly={detail.promoted}
          onChanged={refresh}
          staging={staging}
        />
      )}
    />
  );

  return (
    <WorkspaceCard
      title={counted('Tracts', tracts.length)}
      actions={
        detail.promoted ? undefined : (
          <Button
            kind="tertiary"
            size="sm"
            renderIcon={Add}
            onClick={() => setAdding(true)}
          >
            Add tracts
          </Button>
        )
      }
    >
      {(vestingError || staging.error) && (
        <InlineNotification
          kind="error"
          lowContrast
          title=""
          subtitle={vestingError ?? staging.error ?? undefined}
          onCloseButtonClick={() => {
            clearVestingError();
            staging.clearError();
          }}
        />
      )}
      <div className={styles.tractTables}>
        {level === 'tracts' ? (
          tractTable(tractRows)
        ) : (
          <DataTable<DocumentRow>
            data={documentRows}
            columns={DOCUMENT_COLUMNS}
            getRowId={(row) => row.key}
            config={{
              columnFilters: false,
              expandAllToggle: true,
              sizeToContent: true,
              export: true,
            }}
            leftToolbarActions={[levelDropdown]}
            renderRowSubComponent={(row) => (
              <div className={styles.heldToCard}>
                {tractTable(row.original.tracts, true)}
              </div>
            )}
          />
        )}
      </div>
      {removeVestingConfirmation}
      <AddTractsModal
        id={id}
        open={adding}
        onClose={() => setAdding(false)}
        existing={tractIds}
      />
    </WorkspaceCard>
  );
};

// Workbooks and vesting documents in one table.
type TitleSupportRow = RelatedItemRow & { key: string } & (
    | { support: WorkspaceSupport }
    | {
        support?: undefined;
        tract: WorkspaceTract;
        vesting: WorkspaceVestingRow;
      }
  );

interface RowActions {
  readOnly: boolean;
  undoLink: (changeId: number) => void;
  undoLinkBusy: boolean;
  retry: (supportId: number) => void;
  retryBusy: boolean;
}

const conversionText = (support: WorkspaceSupport) => {
  if (support.conversionError) return support.conversionError;
  if (isConverting(support)) return 'Converting';
  if (support.sheetsFailed > 0)
    return `${format0(support.sheetsFailed)} of ${format0(
      support.sheetsTotal
    )} sheets failed`;
  return 'Converted';
};

const conversionCell = (support: WorkspaceSupport, actions: RowActions) => {
  if (support.conversionError) {
    return (
      <div className={styles.rowActions}>
        <Tag type="red" size="sm">
          {support.conversionError}
        </Tag>
        <RowAction
          renderIcon={Renew}
          iconDescription="Retry conversion"
          tooltipPosition="top"
          tooltipAlignment="end"
          disabled={actions.readOnly || actions.retryBusy}
          onClick={() => actions.retry(support.id)}
        />
      </div>
    );
  }
  if (isConverting(support)) return <InlineLoading description="Converting…" />;
  return (
    <Tag type={support.sheetsFailed > 0 ? 'magenta' : 'green'} size="sm">
      {conversionText(support)}
    </Tag>
  );
};

const tractsCell = (row: TitleSupportRow, actions: RowActions) => {
  if (!row.support) {
    const { tract, vesting } = row;
    return (
      <span className={styles.pills}>
        <Link to={tractHref(tract.salesforceTractId)}>{tract.name}</Link>
        {vesting.pending === 'remove' && (
          <Tag type="teal" size="sm">
            {PENDING_REMOVAL}
          </Tag>
        )}
      </span>
    );
  }
  const { support } = row;
  if (!support.tracts.length) return EMPTY;
  return (
    <span className={styles.pills}>
      {support.tracts.map(({ salesforceTractId, name, pendingChangeId }) => (
        <span key={salesforceTractId}>
          <Link to={tractHref(salesforceTractId)}>{name}</Link>
          {pendingChangeId !== null && (
            <>
              <Tag type="teal" size="sm">
                Pending link
              </Tag>
              <RowAction
                renderIcon={Undo}
                iconDescription="Undo link"
                disabled={actions.readOnly || actions.undoLinkBusy}
                onClick={() => actions.undoLink(pendingChangeId)}
              />
            </>
          )}
        </span>
      ))}
      {support.pendingCuration && (
        <Tag type="teal" size="sm">
          Pending edit
        </Tag>
      )}
    </span>
  );
};

const tractsText = (row: TitleSupportRow) => {
  if (!row.support) return row.tract.name;
  return row.support.tracts.map((tract) => tract.name).join(', ');
};

const statusCell = (row: TitleSupportRow, actions: RowActions) => {
  if (row.support) return conversionCell(row.support, actions);
  return <VestingStatusTag row={row.vesting} />;
};

const statusText = (row: TitleSupportRow) => {
  if (row.support) return conversionText(row.support);
  return vestingStatus(row.vesting)[1];
};

const workbookRow = (support: WorkspaceSupport): TitleSupportRow => ({
  key: `workbook-${support.id}`,
  id: support.externalTitleSupportId,
  kind: support.kind,
  href: support.href ?? undefined,
  title: support.filename,
  fileName: support.filename,
  createdBy: support.createdBy ?? null,
  createdAt: support.attachedAt,
  details: support.details ?? null,
  coversOwnership: support.coversOwnership ?? null,
  downloadable: support.downloadable,
  support,
});

// Listed beside the workbooks, as the tract's Related Items lists them; managed in the tract row.
const vestingItemRow = (
  tract: WorkspaceTract,
  vesting: WorkspaceVestingRow
): TitleSupportRow => ({
  key: `vesting-${vesting.key}`,
  id: vesting.document.id,
  kind: 'vesting_document',
  title: vestingDocumentLabel(vesting),
  url: vesting.document.href,
  createdBy: vesting.createdBy,
  createdAt: vesting.createdAt ?? '',
  details: null,
  downloadable: false,
  tract,
  vesting,
});

// Only a workbook row is editable or removable.
const supportOf = (supports: WorkspaceSupport[], doc: RelatedItem) =>
  supports.find((support) => support.externalTitleSupportId === doc.id);

export const TitleSupportCard: FC<
  CardProps & {
    linkOpen: boolean;
    onLinkOpenChange: (open: boolean) => void;
  }
> = ({ id, detail, linkOpen, onLinkOpenChange }) => {
  const refresh = useRefreshWorkspace();
  // Unmounting the SidePanel open leaves its push on #main-content; clear it.
  useEffect(
    () => () => {
      const pageContent = document.querySelector('#main-content');
      if (pageContent instanceof HTMLElement) {
        pageContent.style.marginInlineEnd = '';
        pageContent.style.marginInlineStart = '';
        pageContent.style.inlineSize = '';
      }
    },
    []
  );
  const retry = useWorkspaceWrite((supportId: number) =>
    postSupportRetry(id, supportId)
  );
  const { mutate: retryMutate, isPending: retryBusy } = retry;
  const undoLink = useWorkspaceWrite((changeId: number) =>
    deletePendingChange(id, 'tract_link', changeId)
  );
  const { mutate: undoLinkMutate, isPending: undoLinkBusy } = undoLink;
  const [panel, setPanel] = useState<{
    mode: 'add' | 'edit';
    document: RelatedItem | null;
    /** The tract a new workbook is linked to; a multi-tract workspace asks. */
    tractId?: string;
  } | null>(null);
  const [removing, setRemoving] = useState<WorkspaceSupport | null>(null);
  const remove = useWorkspaceWrite(
    (support: WorkspaceSupport) => deleteSupport(id, support.id),
    () => setRemoving(null)
  );
  const { reset: resetRemove } = remove;
  const { tracts, supports } = detail.titleSupport;
  const linkTract = tracts[0];
  const rows = useMemo(
    () => [
      ...supports.map(workbookRow),
      ...tracts.flatMap((tract) =>
        tract.vestingDocuments.map((vesting) => vestingItemRow(tract, vesting))
      ),
    ],
    [supports, tracts]
  );
  // A workbook writes through its own first tract; a new link through the tract the user picked.
  const hostTract = panel?.document
    ? supportOf(supports, panel.document)?.tracts[0]
    : (tracts.find((tract) => tract.salesforceTractId === panel?.tractId) ??
      linkTract);
  const openLink = (tractId: string) => {
    setPanel({ mode: 'add', document: null, tractId });
    onLinkOpenChange(true);
  };

  const actions = useMemo<RowActions>(
    () => ({
      readOnly: detail.promoted,
      undoLink: undoLinkMutate,
      undoLinkBusy,
      retry: retryMutate,
      retryBusy,
    }),
    [detail.promoted, undoLinkMutate, undoLinkBusy, retryMutate, retryBusy]
  );

  const columns = useMemo<DataTableColumnDef<TitleSupportRow>[]>(
    () => [
      ...relatedItemColumns<TitleSupportRow>({
        basePath: '',
        page: 1,
        pageSize: rows.length || 1,
        readOnly: detail.promoted,
        onEdit: (doc) => {
          setPanel({ mode: 'edit', document: doc });
          onLinkOpenChange(true);
        },
        canEdit: (doc) => !!supportOf(supports, doc)?.tracts.length,
        actionTooltipPosition: 'left',
        actionTooltipAlignment: 'center',
        onDelete: (doc) => {
          resetRemove();
          setRemoving(supportOf(supports, doc) ?? null);
        },
      }).flatMap<DataTableColumnDef<TitleSupportRow>>((column) => {
        const sized = {
          ...column,
          // The cell's truncating wrapper would clip the tooltip.
          ...(column.id === 'actions' && {
            meta: { ...column.meta, overflowVisible: true },
          }),
          // Wraps, so less of the table sits under the pinned Actions column.
          ...(column.id === 'title' && {
            header: 'Name',
            meta: { ...column.meta, wrap: true },
          }),
        };
        if (column.id !== 'title') return [sized];
        // Ahead of the long title: the pinned Actions column covers the overflow, and these hold the pending state and Undo.
        return [
          {
            id: 'support',
            header: 'Support',
            meta: {
              getExportValue: ({ support }: TitleSupportRow) =>
                (support?.directSupport && 'Direct support') || EMPTY,
            },
            cell: ({ row }) => {
              const { support } = row.original;
              if (!support?.directSupport) return EMPTY;
              return <DirectSupportTag direct />;
            },
          },
          {
            id: 'tracts',
            header: 'Tracts',
            meta: {
              wrap: true,
              overflowVisible: true,
              getExportValue: tractsText,
            },
            cell: ({ row }) => tractsCell(row.original, actions),
          },
          {
            id: 'conversion',
            header: 'Status',
            meta: { getExportValue: statusText },
            cell: ({ row }) => statusCell(row.original, actions),
          },
          sized,
        ];
      }),
    ],
    [
      actions,
      detail.promoted,
      onLinkOpenChange,
      resetRemove,
      rows.length,
      supports,
    ]
  );

  return (
    <WorkspaceCard
      title={counted('Title Support', rows.length)}
      actions={
        tracts.length > 1 && !detail.promoted ? (
          <MenuButton label="Link Item" kind="tertiary" size="sm">
            {tracts.map((tract) => (
              <MenuItem
                key={tract.salesforceTractId}
                label={tract.name}
                onClick={() => openLink(tract.salesforceTractId)}
              />
            ))}
          </MenuButton>
        ) : (
          <Button
            kind="tertiary"
            size="sm"
            renderIcon={Add}
            disabled={!linkTract || detail.promoted}
            onClick={() => openLink(linkTract.salesforceTractId)}
          >
            Link Item
          </Button>
        )
      }
    >
      {Object.entries({ undoLink, retry })
        .filter(([, write]) => write.error)
        .map(([key, write]) => (
          <InlineNotification
            key={key}
            kind="error"
            lowContrast
            title=""
            subtitle={
              readWriteFailure(write.error, UNEXPLAINED_FAILURE) ?? undefined
            }
            onCloseButtonClick={() => write.reset()}
          />
        ))}
      {rows.length ? (
        <DataTable<TitleSupportRow>
          data={rows}
          columns={columns}
          getRowId={(row) => row.key}
          config={{
            columnFilters: false,
            globalSearch: false,
            sizeToContent: true,
            export: true,
          }}
          initialTableState={PIN_ACTIONS}
        />
      ) : (
        <WorkspaceCardNote>
          {linkTract
            ? "No workbook attached. Use Link Item to attach one; workbooks that only mention these tracts in a sheet aren't attached automatically."
            : 'Add a tract to attach a workbook.'}
        </WorkspaceCardNote>
      )}
      {/* Stays mounted: SidePanel clears its push on #main-content only on open→false. */}
      <LinkDocumentPanel
        open={!!linkTract && linkOpen && panel !== null}
        mode={panel?.mode ?? 'add'}
        host={{
          basePath: `${workspacePath(id)}/tracts/${
            hostTract?.salesforceTractId
          }`,
          tractId: hostTract?.salesforceTractId,
          titleSupportWorkspaceId: Number(id),
        }}
        document={panel?.document ?? null}
        addSubtitle={`Add or link an item to ${hostTract?.name}.`}
        onClose={() => onLinkOpenChange(false)}
        onSaved={() => {
          onLinkOpenChange(false);
          refresh();
        }}
        onRefresh={refresh}
      />
      {removing && (
        <DestroyConfirmation
          open
          modalHeading={`Remove ${removing.filename}`}
          size="sm"
          {...confirmProps(remove, 'Could not remove the workbook.', () =>
            setRemoving(null)
          )}
          onRequestSubmit={() => remove.mutate(removing)}
        >
          The workbook is removed from this workspace. Its links on the CRM
          tracts stay.
        </DestroyConfirmation>
      )}
    </WorkspaceCard>
  );
};

export const StandardMorCard: FC<{ id: string } & FilteredProps> = ({
  id,
  filters,
  onRows,
}) => {
  const mor = usePagedTable(id, 'standard_mor', filters, MOR_COLUMNS);
  useReportRows('mor', onRows, mor.rows, mor.facets);
  return (
    <WorkspaceCard title="Ownership">
      <DataTable<OwnershipRow>
        data={mor.rows}
        getRowId={(row, index) => String(row.id ?? `idx-${index}`)}
        {...mor.tableProps}
      />
    </WorkspaceCard>
  );
};
